/* eslint-disable max-lines */
import { useEffect, useRef, useState } from 'react'
import { useMatch, useParams } from 'react-router-dom'
import clsx from 'clsx'
import i18n from 'src/i18n'
import { ComponentSources, ScreenNames } from 'src/utils/datahub/enums'
import { useMediaQuery } from '@hotmart/space-lib/dist/hooks'
import { paths } from 'src/router/paths'
import usePublicCommunity from 'src/hook/usePublicCommunity'
import { useUser } from 'src/hook/useUser'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import usePublicCommunityReactionClick from 'src/hook/usePublicCommunityReactionClick/usePublicCommunityReactionClick'
import { useMentionTrackEvents } from 'src/hook/useMentionTrackEvents'
import { CommunityTypeConsume } from 'src/pages/communityHome'
import { ComponentName, TryResult } from 'src/utils/datahubDefaultEvent'
import { toastEventDispatch } from '@hotmart/space-lib-events/dist/toast'
import { ToastPosition, ToastType } from '@hotmart/space-lib-events/dist/toast/enum'
import { AuthStatus } from '@hotmart/space-lib/dist/enum'
import { ApiError } from 'src/config/request'
import { PostCommentProps } from 'src/pages/CommunityDetails/Posts/posts.types'
import { postComment } from 'src/client'
import { CommentOrigin } from 'src/client/enum'
import { ScreenSizes } from 'src/styles'
import { useClickOutside } from 'src/hook/useClickOutside'
import { ThemeMode } from '@hotmart/space-lib/dist/styles/themes/enum'
import { MediaModal, MentionItem, OnChangeProps, RichTextInput } from '../commentInput/components'
import { OnErrorParams, OnSuccessParams } from '../commentInput/hooks/useMentionUtils'
import { MediaProps, ModalProps } from '../commentInput/commentInput.types'
import { CommentType } from '../postComments'
import { UploadResult } from '../dropZoneUploader'
import { useTrackEvents } from '../commentInput/hooks'
import { MediaCard } from '../mediaCard'
import { CommentsUtils, createRequestPayload } from '../commentInput/commentInput.utils'
import { InputWrapper, Wrapper } from './postCommentInput.styles'
import { PathParams, PostCommentInputProps } from './postCommentInput.types'
import { Actions, Avatar, CommentSuggestion, InputIcons } from './components'

const PostCommentInput = ({
  value = '',
  authorName,
  authorUcode,
  authorRole,
  isAnswer,
  commentSuggestion = false,
  enableMentions,
  className,
  idPost,
  parentCommentId = null,
  allowMedias,
  onSuccess
}: PostCommentInputProps) => {
  const { authStatus, theme } = useUser()
  const { sendOpenActionsEvent, sendCreateCommentEvent, sendMediaEvent } = useTrackEvents()
  const { sendChosenMentionEvent, sendMentionResultsEvent, sendPublishContentMentionEvent } =
    useMentionTrackEvents()
  const { shouldCallReactionClick, handlePublicCommunityReactionClick } =
    usePublicCommunityReactionClick()
  const { setScreenName, storedComment, setComponentSource } = usePublicCommunity()
  const { community } = useCommunityDetails()
  const { idCommunity } = useParams<PathParams>()
  const isPostPage = useMatch(paths.COMMUNITY_POST_PATH)
  const inputRef = useRef<HTMLDivElement>(null)
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.md})`)
  const [comment, setComment] = useState(value)
  const [commentSuggestionText, setCommentSuggestionText] = useState(value)
  const [loading, setLoading] = useState(false)
  const [showActions, setShowActions] = useState(!!value)
  const [mentionCount, setMentionCount] = useState(0)
  const [mediaArray, setMediaArray] = useState<MediaProps[]>()
  const [modalProps, setModalProps] = useState<ModalProps>({
    show: false,
    mediaType: CommentType.TEXT
  })

  const getAuthorFirstName = (fullName: string) => {
    return fullName?.split(/\s+/)[0]
  }

  const isValidComment = comment || storedComment
  const inputContainerRef = useRef<HTMLDivElement>(null)
  const screenName = isPostPage ? ScreenNames.COMMUNITY_POST_DETAIL : ScreenNames.COMMUNITY_FEED
  const currentFile = mediaArray?.[0]?.file
  const disabledMediaActions = loading || Boolean(currentFile)
  const authorFirstName = authorName ? getAuthorFirstName(authorName) : ''
  const placeholder = isAnswer
    ? i18n.t('comments.placeholder_publish_answer')
    : i18n.t('comments.placeholder_publish_comment_mention', {
        author_name: authorFirstName
      })

  /* istanbul ignore next */
  const handleMentionSuccess = ({ query, length }: OnSuccessParams) => {
    sendMentionResultsEvent({
      communityId: Number(community.id),
      communityType: CommunityTypeConsume[community.type],
      communityUserRole: authorRole,
      componentName: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
      componentSource: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
      hasAccessToTheCommunity: true,
      parentEntityId: isPostPage ? String(idPost) : String(idCommunity),
      result: TryResult.SUCCESSFUL,
      resultCount: length,
      searchedString: query
    })
  }

  /* istanbul ignore next */
  const handleMentionError = ({ query, error }: OnErrorParams) => {
    const failureDescription =
      typeof error === 'object' ? JSON.stringify(error)?.slice(0, 255) : error

    sendMentionResultsEvent({
      communityId: Number(community.id),
      communityType: CommunityTypeConsume[community.type],
      communityUserRole: authorRole,
      componentName: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
      componentSource: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
      failureDescription,
      hasAccessToTheCommunity: true,
      parentEntityId: isPostPage ? String(idPost) : String(idCommunity),
      result: TryResult.FAILURE,
      searchedString: query,
      statusCode: error?.statusCode
    })

    toastEventDispatch({
      type: ToastType.DANGER,
      message: i18n.t('comments.mentions.request_error'),
      position: ToastPosition.BOTTOM_RIGHT
    })
  }

  /* istanbul ignore next */
  const handleMentionClick = ({ id, type, query, label, reference }: MentionItem) => {
    const mentionId = String(id).replace(/@.+?#/, '')

    sendChosenMentionEvent({
      communityId: Number(community.id),
      communityType: CommunityTypeConsume[community.type],
      communityUserRole: authorRole,
      componentName: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
      hasAccessToTheCommunity: true,
      parentEntityId: isPostPage ? String(idPost) : String(idCommunity),
      searchedString: query,
      chosenStringHeadline: label,
      chosenStringSubtitle: reference,
      mentionId,
      mentionType: type
    })
  }

  const handleReset = () => {
    setComment('')
    setCommentSuggestionText('')
    setShowActions(false)
    setMediaArray(undefined)
    setModalProps(() => ({
      show: false,
      mediaType: CommentType.TEXT
    }))
  }

  const handleSubmit = async () => {
    sendCreateCommentEvent()

    if (shouldCallReactionClick) {
      setScreenName(screenName)
      setComponentSource(ComponentSources.POST_FIELD)

      if (authStatus === AuthStatus.AUTHENTICATED) {
        handlePublicCommunityReactionClick()

        return
      }

      handlePublicCommunityReactionClick(true, comment)
      return
    }

    setLoading(true)

    try {
      const payload = createRequestPayload({
        media: mediaArray?.[0],
        comment,
        parentCommentId
      }) as PostCommentProps

      const response = await postComment(Number(idPost), payload, CommentOrigin.POST_PAGE)
      const { id } = response

      await onSuccess?.(response)
      handleReset()

      CommentsUtils.sendToastSuccessComment()
      sendPublishContentMentionEvent({
        communityId: Number(community.id),
        communityType: CommunityTypeConsume[community.type],
        communityUserRole: authorRole,
        componentName: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
        componentSource: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
        hasAccessToTheCommunity: true,
        parentEntityId: isPostPage ? String(idPost) : String(idCommunity),
        result: TryResult.SUCCESSFUL,
        createdEntityId: String(id),
        userMentionCount: mentionCount
      })
    } catch (error) {
      const err = error as ApiError
      const failureDescription = typeof err === 'object' ? JSON.stringify(err)?.slice(0, 255) : err
      CommentsUtils.sendToastFailedComment()
      sendPublishContentMentionEvent({
        communityId: Number(community.id),
        communityType: CommunityTypeConsume[community.type],
        communityUserRole: authorRole,
        componentName: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
        componentSource: isPostPage ? ComponentName.POST_COMMENT : ComponentName.POST_FEED_COMMENT,
        failureDescription,
        hasAccessToTheCommunity: true,
        parentEntityId: isPostPage ? String(idPost) : String(idCommunity),
        result: TryResult.FAILURE,
        statusCode: err?.statusCode,
        createdEntityId: 'UNDEFINED',
        userMentionCount: mentionCount
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRichTextInputChange = ({ value, mentions }: OnChangeProps) => {
    setComment(value)
    setMentionCount(mentions?.length || 0)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
    const mentions = document?.querySelectorAll('[data-tippy-root] button')
    const hasMentionList = Boolean(mentions?.length)

    if (event.key === 'Enter' && !hasMentionList) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleCommentSuggestion = (comment: string) => {
    setComment(comment)
    setCommentSuggestionText(comment)
    setShowActions(true)
  }

  const openActions = () => {
    setShowActions(true)
    if (!showActions) sendOpenActionsEvent()
  }

  const handleOpenUploadMediaModal = (mediaType: CommentType) => {
    sendMediaEvent(mediaType)

    return setModalProps({
      show: true,
      mediaType
    })
  }

  const handleClickMediaIcons = (mediaType: CommentType) => {
    handleOpenUploadMediaModal(mediaType)
  }

  const handleCloseModal = (files?: UploadResult[]) => {
    setMediaArray(
      files?.map((file) => ({ ...file, mediaType: modalProps.mediaType }) as MediaProps)
    )

    setModalProps((prevState) => ({
      show: false,
      mediaType: files ? prevState.mediaType : CommentType.TEXT
    }))
  }

  const handleRemoveMedia = (fileName: string) => {
    setModalProps((prevState) => ({
      ...prevState,
      mediaType: CommentType.TEXT
    }))

    setMediaArray((prevMediaArray) => {
      return prevMediaArray?.filter((media) => media.file.name !== fileName)
    })
  }

  useClickOutside(inputContainerRef, (event) => {
    const isFeedCard = (event?.target as HTMLElement)?.closest('[data-post-id]')

    if (!isFeedCard && !isValidComment && showActions && !currentFile) {
      setShowActions(false)
    }
  })

  useEffect(() => {
    if (storedComment) {
      setComment(storedComment)
      inputRef.current?.focus()
    }
  }, [storedComment])

  useEffect(() => {
    if (currentFile) {
      openActions()
    }
  }, [currentFile])

  return (
    <>
      <Wrapper
        ref={inputContainerRef}
        className={clsx('_d-flex', className)}
        data-test="post-comment-input"
        $open={showActions}
      >
        <Avatar small className="_mr-3" />

        <InputWrapper
          className={clsx(
            '_d-flex _flex-column _flex-1 _border _border-gray-400 _position-relative _rounded-lg',
            {
              '_bg-gray-200': theme === ThemeMode.DARK,
              '_bg-gray-100': theme === ThemeMode.LIGHT
            }
          )}
        >
          <RichTextInput
            value={comment}
            placeholder={placeholder}
            commentSuggestionText={commentSuggestionText}
            autoFocus={isAnswer}
            blur={!showActions}
            enableMentions={enableMentions}
            readOnly={loading}
            onChange={handleRichTextInputChange}
            onFocus={openActions}
            onKeyDown={handleKeyDown}
            onMentionSuccess={handleMentionSuccess}
            onMentionError={handleMentionError}
            onMentionClick={handleMentionClick}
          />

          {!isMobile && (
            <InputIcons
              allowMedias={allowMedias}
              show={!showActions}
              onClick={handleClickMediaIcons}
            />
          )}

          {mediaArray?.map((media) => (
            <MediaCard
              key={media.mediaKey}
              className="_bg-gray-400 _m-3"
              mediaType={media.mediaType}
              thumbnailUrl={media.blobUrl}
              mediaKey={media.mediaKey}
              onDelete={handleRemoveMedia}
              {...media.file}
            />
          ))}

          {showActions && (
            <Actions
              loading={loading}
              mediaArray={mediaArray}
              comment={comment}
              allowMedias={allowMedias}
              disabledMediaActions={disabledMediaActions}
              onReset={handleReset}
              onSubmit={handleSubmit}
              onOpenUploadMediaModal={handleOpenUploadMediaModal}
            />
          )}
        </InputWrapper>
      </Wrapper>

      {commentSuggestion && (
        <CommentSuggestion
          authorName={authorName}
          authorUcode={authorUcode}
          disabled={loading}
          onClick={handleCommentSuggestion}
        />
      )}

      <MediaModal {...modalProps} onClose={handleCloseModal} />
    </>
  )
}

export default PostCommentInput
